import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../lib/api';
import { PageHeader, KpiCard, Loading, ErrorBox } from '../components/ui';

export function OverviewPage() {
  const overview = useQuery({ queryKey: ['admin-overview'], queryFn: () => adminApi.overview() });
  const attention = useQuery({ queryKey: ['admin-attention'], queryFn: () => adminApi.attention() });

  if (overview.isLoading) return <Loading />;
  if (overview.error) return <ErrorBox error={overview.error} />;

  const d = overview.data as any;
  const kpis = d?.kpis ?? [];

  return (
    <div>
      <PageHeader title="Overview" subtitle="Everything happening across wheez right now" />

      <div className="grid grid-cols-6 gap-3 mb-5">
        {kpis.map((k: any) => (
          <KpiCard key={k.key} label={k.label ?? k.key} value={k.formatted ?? String(k.value)} delta={k.delta} deltaTone={k.deltaTone} hint={k.hint} />
        ))}
      </div>

      <div className="grid grid-cols-3 gap-4 mb-5">
        <div className="card p-4 col-span-2">
          <div className="font-semibold text-sm mb-1" style={{ color: 'var(--text-heading)' }}>Orders over time</div>
          <div className="text-xs mb-3" style={{ color: 'var(--text-faint)' }}>{d?.series?.granularity ?? ''}</div>
          <MiniChart current={d?.series?.current ?? []} previous={d?.series?.previous ?? []} labels={d?.series?.labels ?? []} />
        </div>
        <div className="card p-4">
          <div className="font-semibold text-sm mb-3" style={{ color: 'var(--text-heading)' }}>Funnel</div>
          <div className="space-y-2">
            {(d?.funnel ?? []).map((f: any) => (
              <div key={f.label} className="flex items-center justify-between text-xs">
                <span style={{ color: 'var(--text-muted)' }}>{f.label}</span>
                <span className="font-semibold" style={{ color: 'var(--text-heading)' }}>{f.value} {f.deltaPct && <span style={{ color: 'var(--danger)' }}>{f.deltaPct}</span>}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card p-4">
        <div className="font-semibold text-sm mb-3" style={{ color: 'var(--text-heading)' }}>Needs attention</div>
        {attention.isLoading ? (
          <Loading />
        ) : (
          <table>
            <thead>
              <tr><th>Issue</th><th>Reference</th><th>Customer</th><th>Store</th><th>Waiting</th></tr>
            </thead>
            <tbody>
              {((attention.data as any)?.items ?? []).map((it: any) => (
                <tr key={it.id ?? it.ref}>
                  <td>{it.issue ?? it.text}</td>
                  <td>{it.ref ?? it.reference}</td>
                  <td>{it.customer}</td>
                  <td>{it.store}</td>
                  <td>{it.waiting}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function MiniChart({ current, previous, labels }: { current: number[]; previous: number[]; labels: string[] }) {
  const w = 600, h = 160, max = Math.max(1, ...current, ...previous);
  const pt = (arr: number[]) => arr.map((v, i) => `${(i / Math.max(1, arr.length - 1)) * w},${h - (v / max) * h}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-40">
      {previous.length > 1 && <polyline points={pt(previous)} fill="none" stroke="#A1A1AA" strokeDasharray="4 4" strokeWidth={2} />}
      {current.length > 1 && <polyline points={pt(current)} fill="none" stroke="var(--brand)" strokeWidth={2.5} />}
      {!current.length && <text x={w / 2} y={h / 2} textAnchor="middle" fill="#A1A1AA" fontSize={12}>No data yet</text>}
    </svg>
  );
}
